import { CommonModule } from '@angular/common';
import { Component, HostListener } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { LogoutService } from 'app/core/auth/logout.service';
import { ApiService } from 'app/shared/api.service';
import Swal from 'sweetalert2';

interface PermissionPayload {
    name: string;
}

interface Permission {
    id: string;
    name: string;
    guard_name?: string;
}

interface PaginationInfo {
    current_page: number;
    per_page: number;
    total: number;
    total_page: number;
}

interface PermissionResponse {
    data: {
        list: Permission[];
        pagination: PaginationInfo;
        is_tenant: boolean;
    }
}

@Component({
    selector: 'app-permission',
    standalone: true,
    imports: [CommonModule, FormsModule],
    templateUrl: './permission.component.html',
    styleUrl: './permission.component.scss',
})
export class PermissionComponent {
    isDropdownOpen = false;
    isLoggingOut = false;
    isModalOpen = false;
    isEditMode = false;
    isSubmitting = false;
    isLoading = false;

    Permission: Permission[] = [];
    pagination: PaginationInfo = {
        current_page: 1,
        per_page: 10,
        total: 0,
        total_page: 0,
    };

    permissionForm: PermissionPayload = {
        name: '',
    };

    selectedRoleId: string = '';

    constructor(
        private logoutService: LogoutService,
        private apiService: ApiService,
        private router: Router
    ) { }

    ngOnInit(): void {
        this.getPermission();
    }

    // ==================== CRUD OPERATIONS ====================

    // GET - List Roles
    async getPermission(page: number = 1): Promise<void> {
        this.isLoading = true;
        try {
            const response: PermissionResponse = await this.apiService.get(
                `/api/v1/manage/permission/list?page=${page}`
            );

            // console.log(response);

            // Assign data list dan pagination
            this.Permission = response.data.list;
            this.pagination = response.data.pagination;

            // console.log('Permissions:', this.Permission);
            // console.log('Pagination:', this.pagination);
        } catch (error) {
            console.error('Error fetching permissions:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.error?.message || 'Terjadi kesalahan saat memuat data permission',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false,
            });
        } finally {
            this.isLoading = false;
        }
    }

    // GET - Single Permission (untuk edit)
    async getPermissionById(permissionId: string): Promise<void> {
        try {
            const response = await this.apiService.get(
                `/api/v1/manage/permission/${permissionId}`
            );
            // console.log(response);

            // Populate form dengan data permission
            this.permissionForm = {
                name: response.data.name,
            };

            this.selectedRoleId = permissionId;
            this.isEditMode = true;
            this.isModalOpen = true;
        } catch (error) {
            console.error('Error fetching permission:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error?.error?.message || 'Gagal memuat data permission',
                confirmButtonColor: '#dc3545',
                timer: 2000,
                showConfirmButton: false,
            });
        }
    }

    // POST - Create Permission
    async createPermission(): Promise<void> {
        if (!this.validateForm()) {
            return;
        }

        this.isSubmitting = true;

        try {
            await this.apiService.post(
                '/api/v1/manage/permission',
                this.permissionForm
            );

            await Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'Permission berhasil ditambahkan',
                confirmButtonColor: '#28a745',
                timer: 2000,
                showConfirmButton: false,
            });

            this.closeModal();
            this.getPermission(this.pagination.current_page);
        } catch (error: any) {
            console.error('Error creating permission:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Gagal',
                text: error?.error?.message || 'Gagal menambahkan permission',
                confirmButtonColor: '#dc3545',
            });
        } finally {
            this.isSubmitting = false;
        }
    }

    // PUT/PATCH - Update Permission
    async updatePermission(): Promise<void> {
        if (!this.validateForm()) {
            return;
        }

        this.isSubmitting = true;

        try {
            await this.apiService.put(
                `/api/v1/manage/permission/${this.selectedRoleId}`,
                this.permissionForm
            );

            await Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'Permission berhasil diupdate',
                confirmButtonColor: '#28a745',
                timer: 2000,
                showConfirmButton: false,
            });

            this.closeModal();
            this.getPermission(this.pagination.current_page);
        } catch (error: any) {
            console.error('Error updating permission:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Gagal',
                text: error?.error?.message || 'Gagal mengupdate permission',
                confirmButtonColor: '#dc3545',
            });
        } finally {
            this.isSubmitting = false;
        }
    }

    // DELETE - Delete Permission
    async deletePermission(
        permissionId: string,
        permissionName: string
    ): Promise<void> {
        const result = await Swal.fire({
            title: 'Konfirmasi Hapus',
            text: `Apakah Anda yakin ingin menghapus permission "${permissionName}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Ya, Hapus',
            cancelButtonText: 'Batal',
        });

        if (result.isConfirmed) {
            try {
                await this.apiService.delete(
                    '/api/v1/manage/permission',
                    permissionId
                );

                await Swal.fire({
                    icon: 'success',
                    title: 'Berhasil',
                    text: 'Permission berhasil dihapus',
                    confirmButtonColor: '#28a745',
                    timer: 2000,
                    showConfirmButton: false,
                });

                // Jika halaman current kosong setelah delete, kembali ke halaman sebelumnya
                if (
                    this.Permission.length === 1 &&
                    this.pagination.current_page > 1
                ) {
                    this.getPermission(this.pagination.current_page - 1);
                } else {
                    this.getPermission(this.pagination.current_page);
                }
            } catch (error: any) {
                console.error('Error deleting permission:', error);
                await Swal.fire({
                    icon: 'error',
                    title: 'Gagal',
                    text: error?.error?.message || 'Gagal menghapus permission',
                    confirmButtonColor: '#dc3545',
                });
            }
        }
    }

    // ==================== PAGINATION ====================

    goToPage(page: number): void {
        if (page >= 1 && page <= this.pagination.total_page) {
            this.getPermission(page);
        }
    }

    nextPage(): void {
        if (this.pagination.current_page < this.pagination.total_page) {
            this.getPermission(this.pagination.current_page + 1);
        }
    }

    previousPage(): void {
        if (this.pagination.current_page > 1) {
            this.getPermission(this.pagination.current_page - 1);
        }
    }

    get canGoPrevious(): boolean {
        return this.pagination.current_page > 1;
    }

    get canGoNext(): boolean {
        return this.pagination.current_page < this.pagination.total_page;
    }

    get startIndex(): number {
        return (
            (this.pagination.current_page - 1) * this.pagination.per_page + 1
        );
    }

    get endIndex(): number {
        return Math.min(
            this.pagination.current_page * this.pagination.per_page,
            this.pagination.total
        );
    }

    get visiblePages(): (number | string)[] {
        const total = this.pagination.total_page;
        const current = this.pagination.current_page;
        const delta = 2;
        const pages: (number | string)[] = [];

        if (total <= 7) {
            for (let i = 1; i <= total; i++) {
                pages.push(i);
            }
        } else {
            if (current <= 4) {
                for (let i = 1; i <= 5; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(total);
            } else if (current >= total - 3) {
                pages.push(1);
                pages.push('...');
                for (let i = total - 4; i <= total; i++) {
                    pages.push(i);
                }
            } else {
                pages.push(1);
                pages.push('...');
                for (let i = current - 1; i <= current + 1; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(total);
            }
        }
        return pages;
    }

    // ==================== MODAL OPERATIONS ====================

    openAddModal(): void {
        this.isEditMode = false;
        this.resetForm();
        this.isModalOpen = true;
    }

    openEditModal(permissionId: string): void {
        this.getPermissionById(permissionId);
    }

    closeModal(): void {
        this.isModalOpen = false;
        this.resetForm();
    }

    resetForm(): void {
        this.permissionForm = {
            name: '',
        };
        this.selectedRoleId = '';
    }

    // ==================== FORM VALIDATION ====================

    validateForm(): boolean {
        if (
            !this.permissionForm.name ||
            this.permissionForm.name.trim() === ''
        ) {
            Swal.fire({
                icon: 'warning',
                title: 'Validasi Gagal',
                text: 'Nama permission wajib diisi',
                confirmButtonColor: '#ffc107',
            });
            return false;
        }

        // Validasi panjang minimal
        if (this.permissionForm.name.trim().length < 3) {
            Swal.fire({
                icon: 'warning',
                title: 'Validasi Gagal',
                text: 'Nama permission minimal 3 karakter',
                confirmButtonColor: '#ffc107',
            });
            return false;
        }

        return true;
    }

    // ==================== HANDLER SUBMIT ====================

    async onSubmit(): Promise<void> {
        if (this.isEditMode) {
            await this.updatePermission();
        } else {
            await this.createPermission();
        }
    }
}
